import { Tooltip } from "antd"
import React from "react"

import type { AnalyticsTopRegionRow } from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR, REGION_LABELS } from "./analytics-constants"

interface TopRegionsCardProps {
  rows: AnalyticsTopRegionRow[]
  onSelectRegion: (region: string) => void
}

const SEGMENT_ORDER = ["project", "lead", "subcontractor", "appointment"]

/**
 * Spec §7: top 5 regions (already capped server-side — only 5 macro-regions
 * exist total, per the architecture doc's own alternatives comparison),
 * total count + stacked distribution by entity type. "Unknown" (missing
 * region) is server-provided and deliberately NOT clickable — the Region
 * filter has no "unknown" value to apply.
 */
export function TopRegionsCard({ rows, onSelectRegion }: TopRegionsCardProps) {
  const maxTotal = Math.max(...rows.map((row) => row.total), 1)

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {rows.map((row) => {
        const label = REGION_LABELS[row.region] ?? row.region
        const isClickable = row.region !== "unknown"
        const barContent = (
          <>
            <Tooltip title={label}>
              <span className="w-20 shrink-0 truncate text-[13px] text-slate-700">
                {label}
              </span>
            </Tooltip>
            {/* A full-width neutral track behind a proportional fill, so the
                relative scale between rows reads at a glance even where one
                region dominates. */}
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="flex h-full overflow-hidden rounded-full"
                style={{ width: `${(row.total / maxTotal) * 100}%` }}
              >
                {SEGMENT_ORDER.filter((key) => row.byEntityType[key]).map(
                  (key) => (
                    <div
                      key={key}
                      style={{
                        width: `${(row.byEntityType[key] / row.total) * 100}%`,
                        backgroundColor: ANALYTICS_ENTITY_COLOR[key]
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <span className="w-7 shrink-0 text-right text-[13px] font-semibold text-slate-900">
              {row.total}
            </span>
          </>
        )

        return (
          <li key={row.region}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => onSelectRegion(row.region)}
                className="flex w-full items-center gap-3 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-slate-50"
              >
                {barContent}
              </button>
            ) : (
              <div className="flex w-full items-center gap-3 px-1.5 py-1.5">
                {barContent}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
